import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CalendarClock, Plus, Trash2, Lock, Target, Timer, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const TOOLS = [
  { id: "service_discovery", name: "Découverte services" },
  { id: "vuln_scripts", name: "Vuln NSE" },
  { id: "cve_correlation", name: "Corrélation CVE" },
  { id: "nuclei", name: "Nuclei" },
  { id: "nikto", name: "Nikto" },
  { id: "whatweb", name: "WhatWeb" },
  { id: "searchsploit", name: "SearchSploit" },
  { id: "subfinder", name: "Subfinder" },
  { id: "httpx", name: "httpx" },
];

const FREQ = [
  { id: "hourly", label: "Toutes les heures" },
  { id: "daily", label: "Chaque jour" },
  { id: "weekly", label: "Chaque semaine" },
];

const PROFILES = ["stealth", "standard", "aggressive", "insane"];

export default function Schedules() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.plan === "pro";
  const [list, setList] = useState([]);
  const [target, setTarget] = useState("127.0.0.1");
  const [profile, setProfile] = useState("standard");
  const [frequency, setFrequency] = useState("daily");
  const [opts, setOpts] = useState({ service_discovery: true, vuln_scripts: true, cve_correlation: true });
  const [busy, setBusy] = useState(false);

  const load = () => api.listSchedules().then(setList).catch(() => {});
  useEffect(() => { if (isPro) load(); }, [isPro]);

  const toggleOpt = (id) => setOpts(p => ({ ...p, [id]: !p[id] }));

  const create = async () => {
    if (!target.trim()) { toast.error("Cible requise"); return; }
    setBusy(true);
    try {
      await api.createSchedule({ target: target.trim(), profile, frequency, ...opts });
      toast.success("Planification créée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally { setBusy(false); }
  };

  const toggle = async (id) => {
    try { await api.toggleSchedule(id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const remove = async (id) => {
    try { await api.deleteSchedule(id); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  if (!isPro) {
    return (
      <div className="px-4 md:px-8 py-16 text-center max-w-lg mx-auto" data-testid="schedules-locked">
        <Lock className="h-10 w-10 text-primary mx-auto mb-3" />
        <h1 className="text-2xl font-bold">Scans planifiés — Pro</h1>
        <p className="text-muted-foreground mt-2 text-sm">Programme des scans récurrents (horaires, quotidiens, hebdomadaires) sur tes cibles autorisées. Réservé à la formule Pro.</p>
        <Button onClick={() => navigate("/pricing")} className="mt-5 font-mono tracking-widest bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="schedules-upgrade-btn">PASSER À PRO</Button>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1200px] mx-auto space-y-6 animate-fade-in" data-testid="schedules-page">
      <div>
        <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 09 · SCANS PLANIFIÉS</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><CalendarClock className="h-8 w-8 text-primary" /> Scans planifiés</h1>
        <p className="text-muted-foreground mt-1 text-sm">Automatise des scans récurrents. Ils se lancent en arrière-plan et apparaissent dans ton historique.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card/60 border-border p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center"><Target className="h-4 w-4 text-primary" /></div>
            <h3 className="font-semibold">Nouvelle planification</h3>
          </div>
          <div>
            <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">CIBLE</Label>
            <Input data-testid="schedule-target-input" value={target} onChange={e => setTarget(e.target.value)} placeholder="192.168.1.0/24 · localhost" className="font-mono mt-1.5 bg-background border-border focus-visible:ring-primary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">PROFIL</Label>
              <select data-testid="schedule-profile-select" value={profile} onChange={e => setProfile(e.target.value)} className="w-full mt-1.5 bg-background border border-border rounded-md h-9 px-2 font-mono text-sm focus:ring-primary">
                {PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">FRÉQUENCE</Label>
              <select data-testid="schedule-frequency-select" value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full mt-1.5 bg-background border border-border rounded-md h-9 px-2 font-mono text-sm focus:ring-primary">
                {FREQ.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] tracking-widest text-muted-foreground mb-2 block">ANALYSES</Label>
            <div className="grid grid-cols-2 gap-2">
              {TOOLS.map(t => (
                <label key={t.id} className="flex items-center gap-2 p-2 rounded-md border border-border bg-background cursor-pointer text-xs">
                  <Switch checked={!!opts[t.id]} onCheckedChange={() => toggleOpt(t.id)} data-testid={`schedule-tool-${t.id}`} />
                  <span className="font-mono">{t.name}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={create} disabled={busy} className="w-full font-mono tracking-widest gap-2 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="schedule-create-btn">
            <Plus className="h-4 w-4" /> {busy ? "..." : "PLANIFIER"}
          </Button>
        </Card>

        <Card className="bg-card/60 border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border font-mono text-[10px] tracking-widest text-muted-foreground">// PLANIFICATIONS ({list.length})</div>
          <div className="divide-y divide-border max-h-[560px] overflow-auto">
            {list.length === 0 && <div className="px-5 py-10 text-center text-muted-foreground font-mono text-sm" data-testid="schedules-empty">// aucune planification</div>}
            {list.map(s => (
              <div key={s.id} className="px-5 py-4 flex items-center gap-3" data-testid={`schedule-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{s.target}</div>
                  <div className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Timer className="h-3 w-3" /> {FREQ.find(f => f.id === s.frequency)?.label || s.frequency} · {s.profile}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5">prochain : {s.next_run ? new Date(s.next_run).toLocaleString() : "—"}</div>
                </div>
                <Badge variant="outline" className={cn("font-mono text-[10px]", s.enabled ? "border-success/50 text-success" : "border-border text-muted-foreground")}>{s.enabled ? "ACTIF" : "PAUSE"}</Badge>
                <Switch checked={s.enabled} onCheckedChange={() => toggle(s.id)} data-testid={`schedule-toggle-${s.id}`} />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(s.id)} data-testid={`schedule-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="bg-card/60 border-primary/30 p-5 flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-primary" /></div>
        <div>
          <div className="font-mono text-[10px] tracking-widest text-primary mb-1">// RAPPEL</div>
          <div className="font-semibold text-sm">Cibles autorisées uniquement</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Les planifications respectent la liste blanche et le mode défensif. Un scan planifié est ignoré si la limite de scans simultanés est atteinte.</p>
        </div>
      </Card>
    </div>
  );
}
